/* eslint-disable class-methods-use-this */
import { UUID } from '../../types';
import * as Sentry from '@sentry/react';
import { MessagingError, isAuthRejection, isUnrecoverableServerError } from './error';
import FirebaseMessaging from './firebase-messaging';
import WebSocketClient from './websocket-client';
import { Severity } from '@sentry/react';

export type FirebaseMessagePayload = {
    data: any;
    from: string;
    notification: {
        title: string;
        body: string;
    };
    priority: 'normal' | 'high' | 'low';
};

export interface MessageEventMap {
    token_registered: boolean;
    requesting_notification_permission: boolean;
    notification_enabled: boolean;
    message: any;
}

export type OnMessageCallback = <K extends keyof MessageEventMap>(
    event: K,
    message: MessageEventMap[K],
) => void;

export type OnErrorCallback = (error: string) => void;

export type MessagingType = 'firebase' | 'websocket';

export interface MessagingOptions {
    messagingType: MessagingType;
}

export interface InitOptions {
    userId: UUID;
    authToken: string | null;
    deviceId: UUID;
}

export default class Messaging {
    private userId: UUID | null = null;
    private authToken: string | null = null;
    private deviceId: UUID | null = null;
    private messagingType: MessagingType = 'websocket';
    private firebaseMessaging: FirebaseMessaging | null = null;
    private websocketClient: WebSocketClient | null = null;
    private onMessageCallback: OnMessageCallback | null = null;
    private onErrorCallback: OnErrorCallback | null = null;
    private onAuthFailureCallback: ((message: string) => void) | null = null;

    isDeviceRegistered: boolean = false;

    constructor(options: MessagingOptions = { messagingType: 'websocket' }) {
        this.messagingType = options.messagingType;
    }

    async init({ userId, deviceId, authToken }: InitOptions) {
        this.userId = userId;
        this.deviceId = deviceId;
        this.authToken = authToken;

        try {
            if (this.messagingType === 'firebase') {
                await this.initFirebase();
            } else {
                await this.initWebsocket();
            }
        } catch (error) {
            if (error instanceof MessagingError) {
                this.onErrorCallback?.(error.message);
            } else {
                this.onErrorCallback?.('Unknown Error');
            }
        }
    }

    async initFirebase() {
        if (!this.userId) {
            throw new MessagingError('User Id is required to establishing websocket connection');
        }

        if (!this.deviceId) {
            throw new MessagingError('Device Id is required to establishing websocket connection');
        }

        this.firebaseMessaging = new FirebaseMessaging({
            userId: this.userId,
            deviceId: this.deviceId,
        });

        this.firebaseMessaging.onRegisterDevice((isDeviceRegistered) => {
            this.isDeviceRegistered = isDeviceRegistered;
        });

        this.firebaseMessaging.onMessage((data) => this.onMessageCallback?.('message', data));
        this.firebaseMessaging.onError((error) => this.onErrorCallback?.(error));
        this.firebaseMessaging.connect();
    }

    async initWebsocket() {
        if (!this.userId) {
            throw new MessagingError('User Id is required to establishing websocket connection');
        }

        if (!this.deviceId) {
            throw new MessagingError('Device Id is required to establishing websocket connection');
        }

        this.websocketClient = new WebSocketClient({
            url: `${process.env.REACT_APP_WEBSOCKET_URL}`,
            userId: this.userId,
            authToken: this.authToken,
            debug: console.log,
            deviceId: this.deviceId,
        });

        this.websocketClient.onRegisterDevice((isDeviceRegistered) => {
            this.isDeviceRegistered = isDeviceRegistered;
        });

        this.websocketClient.onMessage((data) => this.onMessageCallback?.('message', data));

        this.websocketClient.onError((error) => {
            if (!this.onErrorCallback) return;

            let errorMessage = '';

            // 1. Unpack structured errors safely from the client layer
            if (typeof error === 'string') {
                errorMessage = error;
            } else if (error && typeof error === 'object' && 'message' in error) {
                errorMessage = String((error as any).message);
            } else {
                errorMessage = String(error);
            }

            // 2. Clear out formatting residues
            errorMessage = errorMessage.replace(/\\c/g, '').trim();

            // 3. Centralized routing decision boundary for authentication rejections
            if (isAuthRejection(errorMessage)) {
                console.warn('🔒 WebSocket Authentication Failed.');

                // Invoke the forced shutdown to set delay to 0 and kill the loops
                this.websocketClient?.forceShutdown();
                this.websocketClient = null;

                // Let the app react (e.g. toast + redirect to login), passing the
                // sanitized server message so the UI can display it.
                this.onAuthFailureCallback?.('');

                return;
            }

            if (isUnrecoverableServerError(errorMessage)) {
                this.websocketClient?.handleUnrecoverableStompError(errorMessage);
                this.onAuthFailureCallback?.('');
                return;
            }

            Sentry.captureException(new Error(`WebSocket System Error: ${errorMessage}`), {
                level: Severity.Error,
                tags: {
                    userId: this.userId || 'unknown',
                    deviceId: this.deviceId || 'unknown',
                },
            });

            this.onErrorCallback(errorMessage);
        });

        this.websocketClient.connect();
    }

    isNotificationSupported() {
        return 'Notification' in window;
    }

    destroyFirebase() {
        this.firebaseMessaging?.destroy();
        this.firebaseMessaging = null;
    }

    destroyWebsocket() {
        this.websocketClient?.destroy();
        this.websocketClient = null;
    }

    destroy() {
        if (this.messagingType === 'firebase') {
            this.destroyFirebase();
        } else {
            this.destroyWebsocket();
        }
        this.userId = null;
        this.deviceId = null;
        this.isDeviceRegistered = false;
        this.onMessageCallback = null;
        this.onErrorCallback = null;
        this.onAuthFailureCallback = null;
    }

    onMessage(onMessageCallback: OnMessageCallback) {
        this.onMessageCallback = onMessageCallback;
    }

    onError(onErrorCallback: OnErrorCallback) {
        this.onErrorCallback = onErrorCallback;
    }

    onAuthFailure(onAuthFailureCallback: (message: string) => void) {
        this.onAuthFailureCallback = onAuthFailureCallback;
    }
}
